import { multiWindow, openMessageWindow } from "@transport";
import { useEffect, useRef, useState } from "preact/hooks";

import * as nav from "../lib/navigation";
import * as store from "../lib/store";
import * as tabs from "../lib/tabs";
import { WindowControls } from "./WindowControls";
import * as Icon from "./Icons";

/**
 * The strip along the top of the main window.
 *
 * Desktop only, and drawn only once there is more than the mail — a tab bar
 * with one tab in it is a row of chrome that says nothing. The browser build
 * never renders this at all: it has tabs and windows of its own that are better
 * than these, and a message row is a real link so they work. See
 * `ui/MessageList.tsx`.
 *
 * # Dragging
 *
 * Pointer events rather than HTML5 drag-and-drop, for two reasons that both
 * matter. The drag image and drop effects of the native API are a fight to
 * suppress, and — the deciding one — dropping a tab *off* the strip has to be
 * detectable, and native DnD reports leaving the window as an indistinguishable
 * `dragleave` from moving over a gap.
 *
 * Nothing here reads a window position. Dragging a tab out is a gesture within
 * one window, so it works everywhere; dragging a *window* back onto the strip
 * would need to know where two windows are relative to each other, which
 * Wayland cannot answer by design. Re-attaching is a menu item on the detached
 * window instead — a thing somebody can find without being told it exists,
 * which a gesture is not.
 */
export function TabBar() {
  const list = tabs.tabs.value;
  const active = tabs.active.value;
  const [dragging, setDragging] = useState<string | null>(null);
  /**
   * Where a dropped tab would go, as an *insertion point* rather than an index
   * into the list: `2` means "between the second and third tabs". Null while
   * nothing is being dragged.
   */
  const [dropAt, setDropAt] = useState<number | null>(null);
  /** Whether there is more strip than fits, which is what the arrows are for. */
  const [overflowing, setOverflowing] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const strip = useRef<HTMLDivElement>(null);

  /*
   * Watch for the strip outgrowing its room.
   *
   * A `ResizeObserver` rather than a check on tab count, because the window
   * being narrowed overflows a strip that was fitting a moment ago and no tab
   * changed. Both are the same question -- is there more here than fits -- and
   * only one of them is an event this component would otherwise hear about.
   */
  useEffect(() => {
    const bar = strip.current;
    if (!bar) return;
    const measure = () => setOverflowing(bar.scrollWidth > bar.clientWidth + 1);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(bar);
    return () => ro.disconnect();
  }, [list.length]);

  /*
   * Bring the front tab into view.
   *
   * Opening a tab when the strip is already full puts it past the right edge,
   * and restoring a session can put the remembered one anywhere -- so without
   * this somebody is looking at a strip that does not contain the thing they
   * are reading. `nearest` rather than `center` so a tab already visible is
   * left alone: re-centring the strip on every switch would move tabs the
   * person is aiming at.
   */
  useEffect(() => {
    const node = strip.current?.querySelector(".tab.is-active");
    node?.scrollIntoView({ block: "nearest", inline: "nearest" });
  }, [active, list.length]);

  // Two gates, and they are not the same question. `multiWindow` is whether
  // this build has tabs at all -- false in a browser, where a second view of a
  // message is a second copy of the app and belongs to the browser's own tab
  // bar. `hasTabs` is whether there is more than the mail right now. Only the
  // second used to be here, so a double-click in the web build put a tab in the
  // set and this strip appeared inside a page that has no windows to detach to.
  if (!multiWindow || !tabs.hasTabs.value) return null;

  const scrollBy = (direction: -1 | 1) => {
    const bar = strip.current;
    if (!bar) return;
    // Most of a screenful, not all: leaving a tab or so of overlap is what
    // makes it read as moving along the strip rather than jumping to a new one.
    bar.scrollBy({ left: direction * bar.clientWidth * 0.8, behavior: "smooth" });
  };

  return (
    <div class="tab-bar" data-tauri-drag-region="deep">
    <div
      class="tab-strip"
      role="tablist"
      aria-label="Open messages"
      ref={strip}
      /*
        A plain wheel scrolls the strip sideways.

        The scrollbar is hidden -- a bar under the tabs is chrome under chrome --
        so without this the only way past the right edge is a horizontal gesture
        somebody may not have, on a strip whose overflow is invisible. Vertical
        wheel is what a mouse has.

        Only when there is something to scroll, so the page behind is not robbed
        of a wheel event over a strip that fits.
      */
      onWheel={(e) => {
        const bar = strip.current;
        if (!bar || bar.scrollWidth <= bar.clientWidth) return;
        if (e.deltaX !== 0) return;
        e.preventDefault();
        bar.scrollLeft += e.deltaY;
      }}
    >
      {list.map((tab, index) => (
        <Tab
          key={tab.id}
          tab={tab}
          active={tab.id === active}
          dragging={dragging === tab.id}
          /*
            The marker is drawn on the tab either side of the gap rather than
            as an element of its own, because inserting one would change the
            strip's children -- which is what the drop position is measured
            against. A marker that moved the boxes it is measured from is a
            marker that fights the pointer.
          */
          dropBefore={dropAt === index}
          dropAfter={dropAt === list.length && index === list.length - 1}
          onDragStart={() => setDragging(tab.id)}
          onDragOver={setDropAt}
          onDragEnd={() => {
            setDragging(null);
            setDropAt(null);
          }}
          strip={strip}
        />
      ))}
      </div>

      {/*
        Only when there is somewhere to scroll. Arrows that are always there and
        usually inert are two controls teaching somebody they do nothing.
      */}
      {overflowing && (
        <>
          <button class="tab-nav" aria-label="Scroll tabs left" onClick={() => scrollBy(-1)}>
            <Icon.Back />
          </button>
          <button class="tab-nav" aria-label="Scroll tabs right" onClick={() => scrollBy(1)}>
            <Icon.Chevron />
          </button>
        </>
      )}

      {/*
        Always, unlike the arrows. It is the one control that can reach a tab
        whatever the strip is doing -- including the case the arrows exist for,
        where the tab somebody wants is off the edge and they do not know which
        direction it is in. A list of names answers that; two arrows do not.
      */}
      <TabMenu open={menuOpen} onToggle={() => setMenuOpen((v) => !v)} onClose={() => setMenuOpen(false)} />

      {/*
        The window's own buttons, when this build draws them. Here rather than
        as the fixed overlay `app.tsx` uses, because this strip is full width
        and already at the top of the window -- so the controls can be ordinary
        flex children and line up with the tab menu beside them without anybody
        reserving space or agreeing on a height. `app.tsx` renders the overlay
        only when this strip is absent.
      */}
      <WindowControls inline />
    </div>
  );
}

/**
 * Every open tab, by name, in a list.
 *
 * The strip shows what fits; this shows what there is. With enough tabs the
 * strip's labels are truncated to near-uselessness, and this is the only place
 * a full subject can be read -- so it is not merely a shortcut for the arrows.
 */
function TabMenu({
  open,
  onToggle,
  onClose,
}: {
  open: boolean;
  onToggle: () => void;
  onClose: () => void;
}) {
  const list = tabs.tabs.value;
  const active = tabs.active.value;
  const box = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    // Closing on any press outside, and on Escape, because a menu that can only
    // be dismissed by the button that opened it is a menu people click past.
    const onDown = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) onClose();
    };
    /*
     * Escape closes the menu and stops there.
     *
     * Capture, and `stopPropagation`, because the app's own Escape handler is
     * still listening and would act on the mail underneath -- backing out of
     * the reader, or clearing a selection -- while the person was only
     * dismissing a menu. A dismissal should cost exactly the thing being
     * dismissed.
     */
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.stopPropagation();
      onClose();
      dropFocusRing();
    };
    document.addEventListener("pointerdown", onDown, true);
    document.addEventListener("keydown", onKey, true);
    return () => {
      document.removeEventListener("pointerdown", onDown, true);
      document.removeEventListener("keydown", onKey, true);
    };
  }, [open, onClose]);

  return (
    <div class="tab-menu" ref={box}>
      <button
        class="tab-nav"
        aria-label="All open tabs"
        aria-expanded={open}
        aria-haspopup="menu"
        onClick={onToggle}
      >
        <Icon.Chevron class="icon down" />
      </button>
      {open && (
        <div class="tab-menu-list" role="menu">
          {list.map((tab) => {
            const label = tabs.isPinned(tab) ? "Mail" : titleFor(tab);
            return (
              /*
                A row of two controls rather than a button inside a button,
                which is not valid markup and which no screen reader would
                describe as two things.
              */
              <div class="tab-menu-row" key={tab.id}>
                <button
                  class={`tab-menu-item${tab.id === active ? " is-active" : ""}`}
                  role="menuitem"
                  onClick={() => {
                    tabs.active.value = tab.id;
                    onClose();
                  }}
                >
                  {label}
                </button>
                {/*
                  Absent on the mail, exactly as in the strip: it cannot be
                  closed, and a disabled control offers something and then
                  refuses it.

                  The menu stays open afterwards, because closing several is
                  the reason somebody opened a list of them. It disappears by
                  itself once only the mail is left, since the strip does.
                */}
                {!tabs.isPinned(tab) && (
                  <button
                    class="tab-menu-close"
                    aria-label={`Close ${label}`}
                    onClick={() => tabs.close(tab.id)}
                  >
                    <Icon.Close />
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

/** How far off the strip a tab must be dropped to detach rather than reorder. */
const DETACH_DISTANCE = 60;

function Tab({
  tab,
  active,
  dragging,
  dropBefore,
  dropAfter,
  onDragStart,
  onDragOver,
  onDragEnd,
  strip,
}: {
  tab: tabs.Tab;
  active: boolean;
  dragging: boolean;
  dropBefore: boolean;
  dropAfter: boolean;
  onDragStart: () => void;
  onDragOver: (at: number) => void;
  onDragEnd: () => void;
  strip: { current: HTMLDivElement | null };
}) {
  const pinned = tabs.isPinned(tab);
  const label = pinned ? "Mail" : titleFor(tab);

  /*
   * The pinned tab is not draggable and nothing may be dropped in front of it.
   * Those are two rules and this handles the first; `tabs.move` refuses the
   * second, because a drag hits it from the other side -- the mail is not being
   * dragged, something is being dropped past it.
   */
  const onPointerDown = (e: PointerEvent) => {
    if (pinned || e.button !== 0) return;
    const startX = e.clientX;
    const startY = e.clientY;
    let moved = false;
    const node = e.currentTarget as HTMLElement;
    node.setPointerCapture(e.pointerId);

    let target: number | null = null;

    const onMove = (m: PointerEvent) => {
      if (!moved && Math.abs(m.clientX - startX) < 4 && Math.abs(m.clientY - startY) < 4) return;
      if (!moved) {
        moved = true;
        onDragStart();
      }
      const bar = strip.current;
      if (!bar) return;
      target = insertionPoint(bar, m.clientX);
      onDragOver(target);
    };

    /*
     * A drag that is taken away rather than finished.
     *
     * The browser cancels a pointer sequence for reasons this code does not
     * control -- a system gesture, focus lost, a touch becoming a scroll. Without
     * this the listeners stay attached and the strip is left showing a marker
     * and a half-transparent tab for a drag that is over, which is exactly how
     * this looked when a link's native drag was cancelling it.
     */
    const onCancel = () => {
      node.removeEventListener("pointermove", onMove);
      node.removeEventListener("pointerup", onUp);
      node.removeEventListener("pointercancel", onCancel);
      onDragEnd();
    };

    const onUp = (u: PointerEvent) => {
      node.releasePointerCapture(u.pointerId);
      node.removeEventListener("pointermove", onMove);
      node.removeEventListener("pointerup", onUp);
      node.removeEventListener("pointercancel", onCancel);
      const dropped = target;
      onDragEnd();
      if (!moved) return;
      const bar = strip.current;
      if (!bar) return;
      // Dropped clear of the strip: detach. A distance rather than "outside the
      // box" so a drop a few pixels below the strip is a clumsy reorder rather
      // than a window somebody did not ask for -- detaching is the harder
      // gesture to undo.
      const box = bar.getBoundingClientRect();
      if (u.clientY - box.bottom > DETACH_DISTANCE) {
        void detach(tab);
        return;
      }
      if (dropped !== null) {
        /*
         * An insertion point is not an index, and the difference is one place.
         *
         * "Between the second and third" is index 2 while the tab is still in
         * the list -- but if the tab being moved is to the *left* of that gap,
         * taking it out shifts everything after it down by one, so the same gap
         * is index 1 afterwards. Without this a tab dragged rightwards lands
         * one short of where the marker was, which is the kind of wrongness
         * that reads as the drag being imprecise rather than as a bug.
         */
        const from = tabs.tabs.peek().findIndex((t) => t.id === tab.id);
        tabs.move(tab.id, dropped > from ? dropped - 1 : dropped);
      }
    };

    node.addEventListener("pointermove", onMove);
    node.addEventListener("pointerup", onUp);
    node.addEventListener("pointercancel", onCancel);
  };

  return (
    <div
      class={`tab${pinned ? " is-pinned" : ""}${active ? " is-active" : ""}${
        dragging ? " is-dragging" : ""
      }${dropBefore ? " is-drop-before" : ""}${dropAfter ? " is-drop-after" : ""}`}
    >
      {/*
        A link for the same reason a message row is one: it goes somewhere, and
        spelling it out gives the browser's own gestures for free. Here the
        `href` also makes a tab's address copyable, which is how somebody sends
        a colleague the message they are looking at.
      */}
      <a
        class="tab-label"
        role="tab"
        aria-selected={active}
        /*
          A link is draggable by default, and that is what broke this: pressing
          and moving started the browser's own link drag, which fires
          `pointercancel`, stops `pointermove`, and leaves the marker frozen
          where it first appeared. The `href` is here for copying an address and
          for the browser's context menu, not for dragging the link itself.
        */
        draggable={false}
        href={tab.address ? nav.fragmentFor(tab.address) : "#/"}
        onClick={(e) => {
          e.preventDefault();
          tabs.active.value = tab.id;
        }}
        onPointerDown={onPointerDown}
        title={label}
      >
        {label}
      </a>
      {/*
        No close button on the mail, because it cannot be closed. Drawing a
        disabled one would be offering something and then refusing it; drawing
        nothing says the tab is simply not that kind of thing.
      */}
      {!pinned && (
        <button
          class="tab-close"
          aria-label={`Close ${label}`}
          onClick={() => tabs.close(tab.id)}
        >
          <Icon.Close />
        </button>
      )}
    </div>
  );
}

async function detach(tab: tabs.Tab): Promise<void> {
  try {
    await tabs.detach(tab.id, (address) => openMessageWindow(address));
  } catch (e) {
    // The tab is still there -- `tabs.detach` only closes it once the window
    // opened -- so this says what did not happen rather than leaving somebody
    // to notice a message went missing.
    store.toast(`Could not open that message in a window — ${message(e)}`, "error");
  }
}

function message(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/**
 * What to call a tab.
 *
 * The subject if the message is in the list, and the plain word otherwise. A
 * tab is opened from a row the person was looking at, so the subject is
 * normally to hand; a message reached by address after a restart may not be,
 * and "Message" beats an id nobody can read.
 */
function titleFor(tab: tabs.Tab): string {
  if (!tab.address) return "Mail";
  // An attachment names itself. Falling through to the subject would label
  // every attachment of one message identically, which is exactly the case
  // somebody opens two tabs to tell apart.
  if (tab.address.part) return tab.address.part.name;
  const key = `${tab.address.account}:${tab.address.email}`;
  const row = store.rows.value.find((r) => store.keyOf(r) === key);
  // The live row first, so an edited subject (a draft) stays current; then the
  // subject captured when the tab was opened, which survives the message
  // leaving the list or a restart; then a last resort that at least names what
  // the tab is.
  return row?.email.subject || tab.address.subject || "Message";
}

/**
 * Which gap between tabs the pointer is over.
 *
 * By midpoint rather than by which tab the pointer is inside, because the
 * question is *which gap*, not *which tab*: crossing half of a tab is the
 * moment the answer changes, and hit-testing the box instead leaves the
 * marker still while the pointer travels a whole tab's width.
 *
 * Never `0`. Nothing goes in front of the mail -- `tabs.move` refuses it too,
 * but a marker offering a position that will not be honoured is worse than the
 * refusal, because it promises something and then quietly does otherwise.
 */
function insertionPoint(bar: HTMLElement, x: number): number {
  const boxes = [...bar.children].map((child) => child.getBoundingClientRect());
  for (let i = 0; i < boxes.length; i += 1) {
    const box = boxes[i]!;
    if (x < box.left + box.width / 2) return Math.max(i, 1);
  }
  return boxes.length;
}

/**
 * Take the focus ring off a tab control that was clicked, not tabbed to.
 *
 * Clicking leaves focus on the control; a keystroke afterwards makes the
 * browser treat that focus as keyboard-driven and draw the ring. So dismissing
 * a menu with Escape left a blue outline round the button somebody had clicked
 * with a mouse, which reads as the control being stuck rather than as focus.
 *
 * Blurred rather than styled away. Removing the outline would take it from
 * somebody who tabbed here as well, and they are the people it exists for --
 * this only fires on Escape, which is a dismissal, and only for a control in
 * the strip.
 */
function dropFocusRing(): void {
  const focused = document.activeElement;
  if (focused instanceof HTMLElement && focused.closest(".tab-bar")) focused.blur();
}
